import React from 'react';
import { Label } from './ui/label';

/**
 * German driver's license classes (Führerscheinklassen).
 * Source: § 6 FeV (German Fahrerlaubnis-Verordnung).
 * Order: most common for car rental first.
 */
export const LICENSE_CLASSES = [
  { code: 'B', label: 'B', desc: 'PKW bis 3,5 t' },
  { code: 'BE', label: 'BE', desc: 'PKW + Anhänger >750 kg' },
  { code: 'B96', label: 'B96', desc: 'PKW + Anhänger 3,5–4,25 t' },
  { code: 'C1', label: 'C1', desc: 'LKW 3,5–7,5 t' },
  { code: 'C1E', label: 'C1E', desc: 'C1 + Anhänger' },
  { code: 'C', label: 'C', desc: 'LKW > 7,5 t' },
  { code: 'CE', label: 'CE', desc: 'C + Anhänger' },
  { code: 'D1', label: 'D1', desc: 'Bus bis 16 Pers.' },
  { code: 'D1E', label: 'D1E', desc: 'D1 + Anhänger' },
  { code: 'D', label: 'D', desc: 'Bus > 16 Pers.' },
  { code: 'DE', label: 'DE', desc: 'D + Anhänger' },
  { code: 'AM', label: 'AM', desc: 'Mofa / Roller bis 45 km/h' },
  { code: 'A1', label: 'A1', desc: 'Krad 125 ccm' },
  { code: 'A2', label: 'A2', desc: 'Krad bis 35 kW' },
  { code: 'A', label: 'A', desc: 'Krad unbegrenzt' },
  { code: 'L', label: 'L', desc: 'Land-/Forstwirtschaft' },
  { code: 'T', label: 'T', desc: 'Zugmaschinen' },
];

const LicenseClassPicker = ({ value = [], onChange, label = 'Führerscheinklassen', disabled = false }) => {
  const selected = new Set(value || []);

  const toggle = (code) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(Array.from(next));
  };

  return (
    <div data-testid="license-class-picker">
      <Label className="overline">{label}</Label>
      <div className="flex flex-wrap gap-2 mt-1">
        {LICENSE_CLASSES.map((c) => {
          const on = selected.has(c.code);
          return (
            <button
              type="button"
              key={c.code}
              onClick={() => toggle(c.code)}
              disabled={disabled}
              title={c.desc}
              className={`px-2.5 py-1 rounded-sm border text-xs font-semibold transition-colors ${
                on
                  ? 'bg-[#E60000] text-white border-[#E60000]'
                  : 'bg-white text-[#525866] border-[#E2E4E9] hover:border-[#8A94A6]'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`license-class-${c.code}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-[#8A94A6] mt-1">
        Mehrfachauswahl. Hover für Beschreibung. {value?.length ? `${value.length} ausgewählt.` : 'Keine ausgewählt.'}
      </p>
    </div>
  );
};

/** Read-only badge list (e.g. for detail view / customer list) */
export const LicenseClassBadges = ({ classes, size = 'sm' }) => {
  if (!classes || classes.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="license-class-badges">
      {classes.map((c) => (
        <span
          key={c}
          className={`inline-flex items-center font-semibold rounded-sm border border-[#E60000]/30 bg-[#E60000]/5 text-[#E60000] ${
            size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
          }`}
        >
          {c}
        </span>
      ))}
    </div>
  );
};

export default LicenseClassPicker;
